import { RefObject, useEffect, useState } from 'react';

export type PopoverPlacement = { top: number; left: number };

/**
 * Tetikleyici elemente göre bir panelin (portal ile document.body'de render edilen)
 * konumunu hesaplar: altına yerleştirir, yer yoksa üstüne çevirir (flip).
 * ColorSwatchPicker.tsx'teki rAF ile ölç-ve-çevir mekanizmasının genelleştirilmiş hali.
 */
export function usePopoverPosition(
  open: boolean,
  triggerRef: RefObject<HTMLElement | null>,
  panelRef: RefObject<HTMLElement | null>,
  gap = 8
): PopoverPlacement | null {
  const [placement, setPlacement] = useState<PopoverPlacement | null>(null);

  useEffect(() => {
    if (!open) {
      setPlacement(null);
      return;
    }
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rafId = requestAnimationFrame(() => {
      const panel = panelRef.current;
      const r = trigger.getBoundingClientRect();
      if (!panel) {
        setPlacement({ left: r.left, top: r.bottom + gap });
        return;
      }
      const panelHeight = panel.offsetHeight;
      const panelWidth = panel.offsetWidth;
      const spaceBelow = window.innerHeight - r.bottom;
      const openAbove = spaceBelow < panelHeight + gap && r.top >= panelHeight + gap;
      const left = Math.min(r.left, Math.max(8, window.innerWidth - panelWidth - 8));
      setPlacement({
        left,
        top: openAbove ? r.top - panelHeight - gap : r.bottom + gap,
      });
    });
    return () => cancelAnimationFrame(rafId);
  }, [open, triggerRef, panelRef, gap]);

  return placement;
}
